import { useState } from 'react'
import Layout, { Card, Empty } from '@/components/Layout'
import { router } from '@inertiajs/react'
import { useCanWrite } from '@/lib/access'
import { t } from '@/lib/i18n'

interface Entry {
  id: number; entry_type: string; delta: number; note: string | null
  at: string; on: string; backfilled: boolean
  // 仅兑换有意义：家长到底给没给
  is_redemption: boolean; fulfilled: boolean; fulfilled_at: string | null
  // 手动加分/扣分：是谁调整的、有没有被撤销
  is_adjustment: boolean; reversed: boolean; by: string | null
}

// 手动加分 / 扣分。扣分即 response cost，真实代币制的标准组成部分。
// 放在流水页而不是别处：调整本身就是账本事件，该在账本里发生。
function AdjustForm() {
  const [delta, setDelta] = useState('')
  const [note, setNote] = useState('')

  const submit = (sign: 1 | -1) => {
    const amount = Math.abs(Number(delta))
    if (!amount || !note.trim()) return
    router.post('/adjustments', { delta: sign * amount, note: note.trim() },
      { preserveScroll: true, onSuccess: () => { setDelta(''); setNote('') } })
  }

  return (
    <Card title={`✏️ ${t('ledger.adjust_title')}`}>
      <div className="text-[13px] text-muted mb-2.5">
        {t('ledger.adjust_hint')}
      </div>
      <div className="flex gap-2.5 flex-wrap items-center">
        <input
          type="number" min={1} value={delta} onChange={(e) => setDelta(e.target.value)}
          placeholder={t('ledger.amount')} className="w-[90px] border border-line rounded-[10px] px-2.5 py-2 outline-none focus:border-brand"
        />
        <input
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={t('ledger.reason_placeholder')}
          className="flex-1 min-w-[180px] border border-line rounded-[10px] px-2.5 py-2 outline-none focus:border-brand"
        />
        <button onClick={() => submit(1)} className="bg-green text-white font-semibold text-[13px] px-3.5 py-2 rounded-[10px]">＋ {t('ledger.add_points')}</button>
        <button onClick={() => submit(-1)} className="bg-card border border-red-line text-red-strong font-semibold text-[13px] px-3.5 py-2 rounded-[10px]">－ {t('ledger.deduct_points')}</button>
      </div>
    </Card>
  )
}

// 调整行：显示是谁调的，以及能否撤销（撤销是反向记账，原记录保留）
function AdjustmentTag({ entry, canWrite }: { entry: Entry; canWrite: boolean }) {
  if (!entry.is_adjustment) return null

  return (
    <span className="ml-1.5 inline-flex items-center gap-1">
      {entry.by && (
        <span className="text-[11px] px-[7px] py-px rounded-md bg-tint text-muted whitespace-nowrap">
          {entry.by}
        </span>
      )}
      {entry.reversed ? (
        <span className="text-[11px] px-[7px] py-px rounded-md bg-bg text-muted whitespace-nowrap">{t('ledger.reversed')}</span>
      ) : canWrite && (
        <button
          onClick={() => router.delete(`/adjustments/${entry.id}`, { preserveScroll: true })}
          title={t('ledger.reverse_title')}
          className="text-[11px] text-muted hover:text-red"
        >
          {t('ledger.reverse')}
        </button>
      )}
    </span>
  )
}

function fmt(iso: string) {
  const d = new Date(iso); const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

// on 是 ISO 日期（YYYY-MM-DD），按字面格式化为 M/D，避免时区偏移
function fmtDay(iso: string) {
  const [, m, d] = iso.split('-')
  return `${Number(m)}/${Number(d)}`
}

// 兑换行显示兑付状态：账本要答得出「这份奖励到底给没给」，
// 否则「双方都认的账本」就缺了最后一环。
function FulfillmentTag({ entry, canWrite }: { entry: Entry; canWrite: boolean }) {
  if (!entry.is_redemption) return null

  if (entry.fulfilled) {
    return (
      <span className="ml-1.5 inline-flex items-center gap-1">
        <span className="text-[11px] px-[7px] py-px rounded-md bg-green-tint text-green-strong whitespace-nowrap">
          ✓ {t('ledger.fulfilled')}{entry.fulfilled_at ? ` · ${fmtDay(entry.fulfilled_at.slice(0, 10))}` : ''}
        </span>
        {canWrite && (
          <button
            onClick={() => router.delete(`/redemptions/${entry.id}/fulfill`, { preserveScroll: true })}
            title={t('ledger.unfulfill_title')}
            className="text-[11px] text-muted hover:text-red"
          >
            {t('ledger.reverse')}
          </button>
        )}
      </span>
    )
  }

  return (
    <span className="ml-1.5 inline-flex items-center gap-1">
      <span className="text-[11px] px-[7px] py-px rounded-md bg-red-tint text-red-strong whitespace-nowrap">
        ⏳ {t('ledger.owed')}
      </span>
      {canWrite && (
        <button
          onClick={() => router.post(`/redemptions/${entry.id}/fulfill`, {}, { preserveScroll: true })}
          className="text-[11px] text-green font-semibold hover:underline"
        >
          {t('ledger.mark_given')}
        </button>
      )}
    </span>
  )
}

export default function Index({ entries }: { entries: Entry[] }) {
  const canWrite = useCanWrite()

  return (
    <Layout>
      {canWrite && <AdjustForm />}

      <Card title={`📒 ${t('ledger.title')}`} extra={<span className="text-muted text-[13px]">{t('ledger.balance_hint')}</span>}>
        {entries.length ? (
          <table className="w-full border-collapse text-sm">
            <thead><tr className="text-muted text-xs"><th className="text-left p-2 border-b border-line">{t('ledger.col_time')}</th><th className="text-left p-2 border-b border-line">{t('ledger.col_item')}</th><th className="text-right p-2 border-b border-line">{t('ledger.col_points')}</th></tr></thead>
            <tbody>
              {entries.map((l) => (
                <tr key={l.id}>
                  <td className="p-2 border-b border-line text-muted">{fmt(l.at)}</td>
                  <td className="p-2 border-b border-line">
                    {l.note || l.entry_type}
                    {l.backfilled && (
                      <span className="ml-1.5 text-[11px] px-[7px] py-px rounded-md bg-amber-tint text-amber-strong whitespace-nowrap">🗓️ {t('ledger.backfilled')} · {fmtDay(l.on)}</span>
                    )}
                    <FulfillmentTag entry={l} canWrite={canWrite} />
                    <AdjustmentTag entry={l} canWrite={canWrite} />
                  </td>
                  <td className={'p-2 border-b border-line text-right font-bold ' + (l.delta >= 0 ? 'text-green' : 'text-red')}>{l.delta > 0 ? '+' : ''}{l.delta || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <Empty emoji="📒" title={t('ledger.empty_title')}>{t('ledger.empty')}</Empty>}
      </Card>
    </Layout>
  )
}
